import { useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  Alert,
  Button,
  Tabs,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components as SubmissionComponents } from '@aaimplatform/institutional-types/ucc-filing-submission';
import type { components as PaperComponents } from '@aaimplatform/institutional-types/ucc-paper-filing';
import {
  RefreshCw as RefreshIcon,
  RotateCcw as RetryIcon,
} from 'lucide-react';
import { useSubmissions, useRetrySubmission, usePaperFilings } from '../../hooks';
import { uccErrorStatus } from '../../hooks/ucc-response';
import { ABSENT, formatCount, formatDate } from '../../utils/display-format';
import {
  CodeBadge,
  metricCellClass,
  metricGridClass,
  MetricSkeletons,
  PAPER_FILING_STATUS_LABEL,
  PAPER_FILING_STATUS_VARIANT,
  shortId,
  SUBMISSION_STATUS_LABEL,
  SUBMISSION_STATUS_VARIANT,
  UccTable,
  UccTenantScope,
  type SubmissionStatus,
} from './ucc-display';

type FilingSubmission = SubmissionComponents['schemas']['FilingSubmission'];
type PaperFilingPackage = PaperComponents['schemas']['PaperFilingPackage'];

const TAB_ITEMS = [
  { id: 'electronic', label: 'Electronic' },
  { id: 'paper', label: 'Paper' },
];

/** Submission statuses a retry can act on. */
const RETRYABLE: ReadonlySet<SubmissionStatus> = new Set<SubmissionStatus>(['rejected', 'failed']);
const PENDING: ReadonlySet<SubmissionStatus> = new Set<SubmissionStatus>(['queued', 'submitting', 'submitted', 'retrying']);
const REJECTED: ReadonlySet<SubmissionStatus> = new Set<SubmissionStatus>(['rejected', 'failed', 'permanently_failed']);

/**
 * The page-level notice when the submission service answers 404. The
 * filing-submission service has no production deployment and no load-balancer
 * rule yet (GH #1734), so both of this page's reads 404. Before this notice
 * the page showed four 0 metric cards and, after retries, "No submissions
 * yet", which reads as a measured empty pipeline.
 */
function ServiceUnavailableNotice() {
  return (
    <Alert type="warning" title="The filing submission service is not available" className="mb-6">
      The submission service did not answer this page's requests (HTTP 404). It has no production deployment yet,
      so electronic and paper submissions cannot be listed here.
    </Alert>
  );
}

export function UCCSubmissionsPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const [activeTab, setActiveTab] = useState('electronic');

  const submissions = useSubmissions({ tenantId });
  const paper = usePaperFilings({ tenantId });
  const retrySubmission = useRetrySubmission();

  const submissionRows = submissions.data?.data ?? [];
  const paperRows = paper.data?.data ?? [];
  const active = activeTab === 'electronic' ? submissions : paper;
  const serviceMissing = uccErrorStatus(submissions.error) === 404 || uccErrorStatus(paper.error) === 404;

  const electronicColumns = useMemo<ColumnDef<FilingSubmission, unknown>[]>(
    () => [
      {
        accessorKey: 'submissionId',
        header: 'Submission ID',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.submissionId)}</span>,
      },
      {
        accessorKey: 'filingId',
        header: 'Filing ID',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.filingId)}</span>,
      },
      { accessorKey: 'jurisdictionCode', header: 'Jurisdiction' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <CodeBadge code={row.original.status} labels={SUBMISSION_STATUS_LABEL} variants={SUBMISSION_STATUS_VARIANT} />
        ),
      },
      {
        accessorKey: 'submittedAt',
        header: 'Submitted',
        cell: ({ row }) => <span className="text-sm text-secondary">{formatDate(row.original.submittedAt)}</span>,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) =>
          RETRYABLE.has(row.original.status) ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => retrySubmission.mutate(row.original.submissionId)}
              disabled={retrySubmission.isPending}
            >
              <RetryIcon className="mr-1" size={14} />
              Retry
            </Button>
          ) : null,
      },
    ],
    [retrySubmission],
  );

  const paperColumns = useMemo<ColumnDef<PaperFilingPackage, unknown>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Package ID',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.id)}</span>,
      },
      {
        accessorKey: 'filingId',
        header: 'Filing ID',
        cell: ({ row }) => <span className="font-mono text-sm">{shortId(row.original.filingId)}</span>,
      },
      { accessorKey: 'jurisdictionCode', header: 'Jurisdiction' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <CodeBadge code={row.original.status} labels={PAPER_FILING_STATUS_LABEL} variants={PAPER_FILING_STATUS_VARIANT} />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => <span className="text-sm text-secondary">{formatDate(row.original.createdAt)}</span>,
      },
    ],
    [],
  );

  const totalSubmissions = submissions.data?.count;
  const counted = submissionRows.length;
  const metricCount = 4;
  const caption = submissions.error ? METRIC_READ_CAPTIONS.failed : undefined;
  const partial = !caption && totalSubmissions !== undefined && totalSubmissions > counted
    ? `Of the ${formatCount(counted)} most recent`
    : undefined;
  const statusCount = (statuses: ReadonlySet<SubmissionStatus>) =>
    caption ? ABSENT : formatCount(submissionRows.filter((s) => statuses.has(s.status)).length);

  return (
    <div>
      <PageHeader
        description="Filing submission pipeline"
        actions={
          <Button variant="secondary" onClick={() => active.refetch()} disabled={active.isFetching || !tenantId}>
            <RefreshIcon className={`mr-2 ${active.isFetching ? 'animate-spin' : ''}`} size={16} />
            Refresh
          </Button>
        }
      />

      <UccTenantScope tenantId={tenantId} />

      {tenantId && serviceMissing && <ServiceUnavailableNotice />}

      {tenantId && (
        <>
          {activeTab === 'electronic' && (
            <div className={`${metricGridClass(metricCount)} mb-6`}>
              {submissions.isLoading ? (
                <MetricSkeletons count={metricCount} />
              ) : (
                <>
                  <MetricCard
                    label="Total Submissions"
                    value={caption || totalSubmissions === undefined ? ABSENT : formatCount(totalSubmissions)}
                    caption={caption}
                    className={metricCellClass(0, metricCount)}
                  />
                  <MetricCard label="Pending" value={statusCount(PENDING)} caption={caption} description={partial} className={metricCellClass(1, metricCount)} />
                  <MetricCard
                    label="Acknowledged"
                    value={statusCount(new Set<SubmissionStatus>(['acknowledged']))}
                    caption={caption}
                    description={partial}
                    className={metricCellClass(2, metricCount)}
                  />
                  <MetricCard label="Rejected or Failed" value={statusCount(REJECTED)} caption={caption} description={partial} className={metricCellClass(3, metricCount)} />
                </>
              )}
            </div>
          )}

          <Tabs
            tabs={TAB_ITEMS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            aria-label="Submission channel"
            panelClassName="mt-4"
          >
            {activeTab === 'electronic' ? (
              <UccTable<FilingSubmission>
                columns={electronicColumns}
                rows={submissionRows}
                isLoading={submissions.isLoading}
                error={submissions.error}
                errorTitle="Could not load electronic submissions"
                onRetry={() => submissions.refetch()}
                empty={{
                  title: 'No electronic submissions for this tenant yet',
                  description: 'A submission appears here once a filing is dispatched to a jurisdiction electronically.',
                }}
                getRowId={(row) => row.submissionId}
              />
            ) : (
              <UccTable<PaperFilingPackage>
                columns={paperColumns}
                rows={paperRows}
                isLoading={paper.isLoading}
                error={paper.error}
                errorTitle="Could not load paper filings"
                onRetry={() => paper.refetch()}
                empty={{
                  title: 'No paper filings for this tenant yet',
                  description: 'A paper package appears here once a filing targets a jurisdiction without electronic filing.',
                }}
                getRowId={(row) => row.id}
              />
            )}
            {retrySubmission.error && (
              <Alert type="error" title="The retry did not start" className="mt-4">
                {retrySubmission.error.message}
              </Alert>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}
